import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';
import {ClaimExpired, InvalidRepairId, NoRepairFound} from './components/claim-expired';
import {Loading} from './components/loading';
import {RepairTracking} from './components/repair';
import {HQSettingsProvider} from './context/hq-settings-context';
import {PageTextProvider} from './context/page-text-context';
import {RepairProvider} from './context/repair-context';
import {useGetHqSettingsQuery, useGetPageTextQuery, useGetDynamicContentQuery} from './features/api/apiSlice';
import {useGetRepairQuery} from './features/api/repairApiSlice';
import {setHqSettingsNeededDays} from './features/scheduling/schedulerSlice';
import './index.css';
import {Footer} from './layouts/footer';
import {Header} from './layouts/header';

const App = () => {
  const repairId = useParams().id;
  const {data: repairData, isFetching: repairIsFetching, refetch} = useGetRepairQuery(repairId);
  const {data: pageTextData, isFetching: pageTextIsFetching} = useGetPageTextQuery();
  const {data: hqSettingsData, isFetching: hqSettingsIsFetching} = useGetHqSettingsQuery();
  const groupSlug = repairData?.group?.slug;
  const {
    data: dynamicContent,
    isUninitialized: dynamicContentIsUninitialized,
    isFetching: dynamicContentIsFetching,
  } = useGetDynamicContentQuery(groupSlug, {
    skip: !groupSlug,
  });
  const dispatch = useDispatch();

  useEffect(() => {
    if (hqSettingsData) {
      dispatch(setHqSettingsNeededDays({hqSettingsNeededDays: hqSettingsData.numberOfDaysToFetchForCustomerSchedule}));
    }
  }, [hqSettingsData, dispatch]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const referringSite = document.referrer;
      const webRepairUrls = ['localhost:3001', 'dev-service.fixt.co', 'model-service.fixt.co', 'service.fixt.co'];
      const referrerIsWebRepair = webRepairUrls.some((site) => referringSite.includes(site));

      if (referrerIsWebRepair) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      } else {
        return;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const loading =
    repairIsFetching ||
    pageTextIsFetching ||
    hqSettingsIsFetching ||
    (groupSlug && (dynamicContentIsUninitialized || dynamicContentIsFetching));

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loading />
      </div>
    );
  }

  const {repair: {expired} = {}} = repairData || {};
  const isExpired = expired ?? false;

  if (!repairId) {
    return <InvalidRepairId />;
  } else if (!repairData || (groupSlug && !dynamicContentIsUninitialized && !dynamicContent)) {
    return <NoRepairFound />;
  } else if (isExpired) {
    return <ClaimExpired />;
  }

  return (
    <PageTextProvider value={pageTextData}>
      <HQSettingsProvider value={hqSettingsData}>
        <RepairProvider value={{...repairData, dynamicContent, refetch}}>
          <div className={styles.container}>
            <Header />
            <RepairTracking />
            <Footer />
          </div>
        </RepairProvider>
      </HQSettingsProvider>
    </PageTextProvider>
  );
};

const styles = {
  loadingContainer: 'flex justify-center items-center h-screen w-screen',
  container: 'h-full w-full grid grid-rows-[65px_auto_100px] grid-cols-1 bg-white text-base font-roboto gap-4',
};

export default App;
